import { useState } from "react";
import { createAdminProfile, updateAdminProfile, deleteAdminProfile } from "../services/profileApi.jsx";

interface Profile {
  profile_id: string;
  backend_config: {
    collections: string[];
    prompt: Array<[string, string]>;
  };
}

interface ProfileData {
  profileName: string;
  collections: string[];
  includeHistory: boolean;
  systemInstruction: string;
}

interface AlertConfig {
  open: boolean;
  type: "success" | "error" | "loading";
  message: string;
}

export const useProfileOperations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreateProfile = async (
    profileData: ProfileData,
    formatProfileForAPI: (sanitizedProfileId?: string) => any,
    onSuccess: () => void,
    setAlertConfig: (config: AlertConfig) => void
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setError("");

      const newProfile = formatProfileForAPI();
      await createAdminProfile(newProfile);
      
      setAlertConfig({
        open: true,
        type: "success",
        message: "Perfil creado exitosamente",
      });

      setError("");
      
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      console.error("Error al crear perfil:", error);
      setAlertConfig({
        open: true,
        type: "error",
        message: "Error al crear el perfil",
      });
      setError("Error al crear el perfil. Por favor, intente nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (
    profileToEdit: Profile,
    profileData: ProfileData,
    formatProfileForAPI: (sanitizedProfileId?: string) => any,
    onSuccess: () => void,
    setAlertConfig: (config: AlertConfig) => void
  ): Promise<void> => {
    try {
      if (!profileToEdit) return;
      
      setIsLoading(true);
      setError("");

      const profileId = profileToEdit.profile_id;
      const updatedProfile = formatProfileForAPI(profileId);

      await updateAdminProfile(profileId, updatedProfile);
      
      setAlertConfig({
        open: true,
        type: "success",
        message: "Perfil actualizado exitosamente",
      });

      setError("");
      
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      console.error("Error al actualizar perfil:", error);
      setAlertConfig({
        open: true,
        type: "error",
        message: "Error al actualizar el perfil",
      });
      setError("Error al actualizar el perfil. Por favor, intente nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProfile = async (
    profileId: string,
    onSuccess: () => void,
    setAlertConfig: (config: AlertConfig) => void
  ): Promise<void> => {
    try {
      setIsLoading(true);
      
      setAlertConfig({
        open: true,
        type: "loading",
        message: "Eliminando perfil...",
      });
      
      await deleteAdminProfile(profileId);
      
      setAlertConfig({
        open: true,
        type: "success",
        message: "Perfil eliminado exitosamente",
      });
      
      onSuccess();
    } catch (error) {
      console.error("Error al eliminar perfil:", error);
      setAlertConfig({
        open: true,
        type: "error",
        message: "Error al eliminar el perfil",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateProfileData = (profileData: ProfileData): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!profileData.profileName.trim()) {
      errors.push("El nombre del perfil es obligatorio");
    }

    if (profileData.collections.length === 0) {
      errors.push("Debe seleccionar al menos una colección");
    }

    const invalidCharacters = /[^a-zA-Z0-9_\-\s]/;
    if (invalidCharacters.test(profileData.profileName)) {
      errors.push("El nombre del perfil contiene caracteres no válidos");
    }

    if (profileData.profileName.length > 50) {
      errors.push("El nombre del perfil no puede exceder 50 caracteres");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const checkProfileNameExists = (profileName: string, existingProfiles: Profile[], excludeId?: string): boolean => {
    const sanitizedName = profileName.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "_");
    
    return existingProfiles.some(profile => 
      profile.profile_id === sanitizedName && profile.profile_id !== excludeId
    );
  };

  const getSanitizedProfileId = (profileName: string): string => {
    return profileName.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "_");
  };

  const clearError = (): void => {
    setError("");
  };

  const setErrorMessage = (message: string): void => {
    setError(message);
  };

  return {
    isLoading,
    error,
    
    handleCreateProfile,
    handleUpdateProfile,
    handleDeleteProfile,
    
    validateProfileData,
    checkProfileNameExists,
    
    getSanitizedProfileId,
    clearError,
    setErrorMessage,
    setError
  };
};